import { firstNameProblem, type CompanionNamingState, type DialogueCloseReason } from "odyssey-shared";
import type { DialogueLine } from "../connection.ts";

/**
 * The dialogue window's state (ADR-136 decision 5.6), owned by the game rather than by React, as the reading panel's
 * is. The server holds the conversation (decision 5.4): a `dialogue` opens the window or moves it on to the node it
 * names, and a `dialogueClose` for the NPC shown closes it, whatever the reason (the player chose an ending, walked
 * away, entered combat, the NPC left or the world swapped). The player chooses by a choice's place among those shown,
 * which the store sends with the node it was shown at, and leaves by Escape or the window's Leave, which sends a close
 * and closes the window at once. Nothing is decided here: a choice is sent and the window waits for the server's
 * answer, so a stale choice is the server's to drop. Listeners are told only on a change, and the snapshot is replaced
 * on each change and kept otherwise, as `useSyncExternalStore` needs.
 *
 * @example
 * ```ts
 * const dialogue = new DialogueStore();
 * dialogue.bind((handle, node, choice) => connection.sendDialogueChoose(handle, node, choice), (handle) => connection.sendDialogueClose(handle));
 * dialogue.receive(line);
 * dialogue.choose(0);
 * ```
 */

/**
 * The companion's naming at the offer as the window shows it (ADR-142 decision 5.4, P2.26-T7): why the last name was
 * refused, null for none, and whether a name is on its way to the server.
 */
export interface NamingView {
  readonly refused: "shape" | "reserved" | "taken" | null;
  readonly pending: boolean;
}

/**
 * One node shown: the line the server sent, a number that changes with every node, so the view starts each from its
 * first choice, and the companion's naming while the server asks for a name, null otherwise.
 */
export interface ShownDialogue {
  readonly line: DialogueLine;
  readonly serial: number;
  readonly naming: NamingView | null;
}

/**
 * What the window's view reads: what to show, or null while closed, and a way to hear that it changed. Both work
 * unbound, as React calls them.
 */
export interface DialogueSource {
  readonly subscribe: (listener: () => void) => () => void;
  readonly getSnapshot: () => ShownDialogue | null;
}

/**
 * What the window's view does: choose one of the choices shown, or leave.
 */
export interface DialogueControl extends DialogueSource {
  /**
   * Chooses a choice by its place among those shown.
   *
   * @param index The choice's index, from 0.
   * @returns True when the window shows such a choice and the choice was sent.
   */
  choose(index: number): boolean;
  /**
   * Leaves the conversation: sends a close and closes the window.
   */
  leave(): void;
  /**
   * Sends the companion's name while the window asks for one and no name is on its way.
   *
   * @param name The name as typed.
   * @returns True when it was sent.
   */
  submitName(name: string): boolean;
}

/**
 * What the window tells the sound (the amendment for P2.22-T36): a conversation opened, a later node shown, and a
 * conversation ended, each with the NPC's handle.
 */
export interface DialogueSounds {
  /** A conversation opened with the NPC; `line` is its first node. */
  opened(line: DialogueLine): void;
  /** A later node of the same conversation was shown. */
  spoke(line: DialogueLine): void;
  /** The conversation with the NPC ended with a farewell: the player left, or the server closed it as done or walked away. */
  farewell(handle: number): void;
}

/**
 * What the session tells the window: each `dialogue` and `dialogueClose`, and that it should close as the session ends.
 */
export interface DialogueFeed {
  /**
   * Whether the window is open.
   */
  readonly open: boolean;
  /**
   * Takes a `dialogue`.
   *
   * @param line The node the server sent.
   */
  receive(line: DialogueLine): void;
  /**
   * Takes a `dialogueClose` from the server.
   *
   * @param handle The NPC whose conversation closed.
   * @param reason Why.
   */
  closed(handle: number, reason: DialogueCloseReason): void;
  /**
   * Takes a `companionNaming`: the server asks for the companion's name, refused one, or ended the naming.
   *
   * @param state Where the naming stands.
   */
  naming(state: CompanionNamingState): void;
  /**
   * Closes the window with nothing sent, as the session ends.
   */
  close(): void;
  /**
   * Hands the window the sound it tells of each conversation's opening, nodes and ending.
   *
   * @param sounds The sound, or null for none.
   */
  listen(sounds: DialogueSounds | null): void;
  /**
   * Hands the window the way to send on the current connection.
   *
   * @param choose Sends a `dialogueChoose`; returns whether it was sent.
   * @param leave Sends a `dialogueClose`.
   * @param name Sends a `companionName`; returns whether it was sent. None sends nothing.
   */
  bind(choose: (handle: number, node: string, choice: number) => boolean, leave: (handle: number) => void, name?: (name: string) => boolean): void;
}

/**
 * The store.
 */
export class DialogueStore implements DialogueControl, DialogueFeed {
  private readonly listeners = new Set<() => void>();
  private current: ShownDialogue | null = null;
  private serial = 0;
  private sendChoose: (handle: number, node: string, choice: number) => boolean = () => false;
  private sendLeave: (handle: number) => void = () => undefined;
  private sendName: (name: string) => boolean = () => false;
  private sounds: DialogueSounds | null = null;

  /**
   * Whether the window is open.
   *
   * @returns True while it shows a node.
   */
  public get open(): boolean {
    return this.current !== null;
  }

  /**
   * Listens for changes.
   *
   * @param listener Called after each change.
   * @returns A function that stops listening.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * What the window shows.
   *
   * @returns The node, or null while closed.
   */
  public readonly getSnapshot = (): ShownDialogue | null => this.current;

  /**
   * Hands the window the sound it tells of each conversation's opening, nodes and ending.
   *
   * @param sounds The sound, or null for none.
   */
  public listen(sounds: DialogueSounds | null): void {
    this.sounds = sounds;
  }

  /**
   * Hands the window the way to send.
   *
   * @param choose Sends a `dialogueChoose`.
   * @param leave Sends a `dialogueClose`.
   * @param name Sends a `companionName`; none sends nothing.
   */
  public bind(choose: (handle: number, node: string, choice: number) => boolean, leave: (handle: number) => void, name: (name: string) => boolean = () => false): void {
    this.sendChoose = choose;
    this.sendLeave = leave;
    this.sendName = name;
  }

  /**
   * Shows a node, replacing whatever the window shows: a conversation with another NPC replaces this one, since the
   * server holds one a session and closes the first.
   *
   * @param line The node.
   */
  public receive(line: DialogueLine): void {
    if (this.current !== null && this.current.line.handle === line.handle) {
      if (this.current.line.node !== line.node) {
        this.sounds?.spoke(line);
      }
    } else {
      this.sounds?.opened(line);
    }
    this.serial += 1;
    this.set({ line, serial: this.serial, naming: null });
  }

  /**
   * Closes the window when the conversation closed is the one shown; a close for another NPC is a conversation the
   * window has already left.
   *
   * @param handle The NPC.
   * @param reason Why; the window closes the same way for every reason, and the NPC bids farewell when it was done or walked away.
   */
  public closed(handle: number, reason: DialogueCloseReason): void {
    if (this.current !== null && this.current.line.handle === handle) {
      if (reason === "done" || reason === "walked") {
        this.sounds?.farewell(handle);
      }
      this.set(null);
    }
  }

  /**
   * Takes where the companion's naming stands: `ask` opens the name field, a refusal shows why with the field open
   * again, and `named` or `closed` ends the naming (the server closes the conversation after it). Nothing changes while
   * the window is closed.
   *
   * @param state The state.
   */
  public naming(state: CompanionNamingState): void {
    const current = this.current;
    if (current === null) {
      return;
    }
    if (state === "ask" || state === "shape" || state === "reserved" || state === "taken") {
      this.set({ ...current, naming: { refused: state === "ask" ? null : state, pending: false } });
    } else if (current.naming !== null) {
      this.set({ ...current, naming: null });
    }
  }

  /**
   * Sends the companion's name while the window asks for one and none is on its way. A name the first-name rule
   * refuses is shown refused at once and not sent, as the server would refuse it; the server judges the rest.
   *
   * @param name The name as typed, trimmed of spaces at its ends.
   * @returns True when it was sent.
   */
  public submitName(name: string): boolean {
    const current = this.current;
    if (current === null || current.naming === null || current.naming.pending) {
      return false;
    }
    const trimmed = name.trim();
    const problem = firstNameProblem(trimmed);
    if (problem !== null) {
      this.set({ ...current, naming: { refused: problem, pending: false } });
      return false;
    }
    if (!this.sendName(trimmed)) {
      return false;
    }
    this.set({ ...current, naming: { refused: null, pending: true } });
    return true;
  }

  /**
   * Sends a choice by its place among those shown.
   *
   * @param index The choice's index, from 0.
   * @returns True when the window shows such a choice and it was sent.
   */
  public choose(index: number): boolean {
    const current = this.current;
    if (current === null || !Number.isInteger(index) || index < 0 || index >= current.line.choices.length) {
      return false;
    }
    return this.sendChoose(current.line.handle, current.line.node, index);
  }

  /**
   * Leaves the conversation shown: sends a close and closes the window. The window closes even when the close could
   * not be sent (no ready socket); the server then keeps the conversation until the player walks away or the session
   * ends, which costs nothing and shows nothing.
   */
  public leave(): void {
    const current = this.current;
    if (current !== null) {
      this.sendLeave(current.line.handle);
      this.sounds?.farewell(current.line.handle);
      this.set(null);
    }
  }

  /**
   * Closes the window with nothing sent.
   */
  public close(): void {
    if (this.current !== null) {
      this.set(null);
    }
  }

  /**
   * Replaces what the window shows and tells every listener.
   *
   * @param next The node, or null to close.
   */
  private set(next: ShownDialogue | null): void {
    this.current = next;
    for (const listener of this.listeners) {
      listener();
    }
  }
}
